import { CommentsFactory } from '@getaccept/lib-shared-new/src/contextual-commenting/helpers/comments.factory';
import { CommentsHelper } from '@getaccept/lib-shared-new/src/contextual-commenting/helpers/comments.helper';
import { useContextualCommentingStore } from '@getaccept/lib-shared-new/src/contextual-commenting/store/contextual-commenting.store';
import type {
  Reply,
  Comment,
} from '@getaccept/lib-shared-new/src/contextual-commenting/types/comment';
import type { CommentParticipant } from '@getaccept/lib-shared-new/src/contextual-commenting/types/comment-participant';
import type { CommentEditorSelection } from '@getaccept/lib-shared-new/src/contextual-commenting/types/comment-selection';
import { CommentStatus } from '@getaccept/lib-shared-new/src/contextual-commenting/types/comment-status';
import type { CreateCommentDto } from '@getaccept/lib-shared-new/src/contextual-commenting/types/create-comment-dto';
import type { CreateReplyInput } from '@getaccept/lib-shared-new/src/contextual-commenting/types/create-reply-input';
import type { SetContextualCommentAndRepliesReadbyInput } from '@getaccept/lib-shared-new/src/contextual-commenting/types/set-contextual-comment-and-replies-readby-input';
import type { UpdateCommentDto } from '@getaccept/lib-shared-new/src/contextual-commenting/types/update-comment-dto';
import { useToast } from '@getaccept/lib-shared-new/src/toast/toast.composable';
import { unref } from 'vue';
import type { MaybeRef } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import { CommentsService } from '../../../api/contextual-commenting/services/comments.service';

export const useContextualCommentingData = () => {
  const { t } = useI18n();
  const contextualCommentingStore = useContextualCommentingStore();

  const loadComments = async (statuses?: MaybeRef<CommentStatus[]>) => {
    try {
      contextualCommentingStore.setComments(
        await CommentsService.load(unref(statuses) as CommentStatus[])
      );
    } catch (e) {
      console.error('Could not load comments', e);
    }
  };

  const { toast } = useToast();

  const createComment = async (
    editorSelection: MaybeRef<CommentEditorSelection>,
    pageId: MaybeRef<string>,
    createdBy: CommentParticipant
  ) => {
    const payload: CreateCommentDto = {
      richText: '',
      text: '',
      pageId: unref(pageId) as string,
      status: CommentStatus.Draft,
      editorSelection: unref(editorSelection) as CommentEditorSelection,
    };

    const readEvent = CommentsFactory.readEvent({ participant: createdBy });
    const temporaryComment: Comment = CommentsFactory.comment({
      ...payload,
      createdBy,
      reads: [readEvent],
      isTemporary: true,
    });
    contextualCommentingStore.addComment(temporaryComment);
    contextualCommentingStore.selectComment(temporaryComment.id);
    try {
      const createdComment = await CommentsService.create(payload);

      // Comment might have been added from Pusher event
      if (CommentsHelper.commentExists(contextualCommentingStore.comments, createdComment.id)) {
        selectComment(temporaryComment.id, createdBy.recipientId);
        contextualCommentingStore.removeComment(temporaryComment.id);
        return;
      }

      contextualCommentingStore.replaceComment(createdComment, temporaryComment.id);
      contextualCommentingStore.selectComment(createdComment.id);
    } catch (e) {
      contextualCommentingStore.selectComment('');
      contextualCommentingStore.removeComment(temporaryComment.id);
      toast.danger(t('could_not_create_contextual_comment'));
    }
  };

  const updateComment = async (payload: MaybeRef<Partial<UpdateCommentDto>>) => {
    const commentPartial = unref(payload) as Partial<UpdateCommentDto>;
    if (
      CommentsHelper.isCommentTemporary(
        contextualCommentingStore.comments,
        commentPartial.commentId
      )
    ) {
      return;
    }

    const staleComment = CommentsHelper.findComment(
      contextualCommentingStore.comments,
      commentPartial.commentId
    );

    if (commentPartial.richText && staleComment.status === CommentStatus.Draft) {
      contextualCommentingStore.setDraftIdUpdating(commentPartial.commentId);
    }

    if (
      commentPartial.status === CommentStatus.Resolved &&
      contextualCommentingStore.selectedCommentId === commentPartial.commentId
    ) {
      contextualCommentingStore.selectComment('');
    }

    contextualCommentingStore.updateComment(commentPartial);

    try {
      await CommentsService.update(commentPartial);
      if (commentPartial.richText && staleComment.status === CommentStatus.Draft) {
        contextualCommentingStore.setDraftIdUpdated(commentPartial.commentId);
        contextualCommentingStore.setDraftIdUpdating('');
        setTimeout(() => contextualCommentingStore.setDraftIdUpdated(''), 700);
      }
    } catch (e) {
      if (staleComment) {
        contextualCommentingStore.updateComment(staleComment);
      }
    }
  };

  const createReply = async (
    payload: MaybeRef<CreateReplyInput>,
    createdBy: CommentParticipant
  ) => {
    const createReplyInput = unref(payload) as CreateReplyInput;
    if (
      CommentsHelper.isCommentTemporary(
        contextualCommentingStore.comments,
        createReplyInput.commentId
      )
    ) {
      return;
    }
    const readEvent = CommentsFactory.readEvent({ participant: createdBy });
    const temporaryReply = CommentsFactory.reply({ ...payload, createdBy, reads: [readEvent] });
    contextualCommentingStore.addReply(createReplyInput.commentId, temporaryReply);

    try {
      const createdReply: Reply = await CommentsService.createReply(createReplyInput);
      contextualCommentingStore.replaceReply(
        createReplyInput.commentId,
        createdReply,
        temporaryReply.id
      );
    } catch (e) {
      contextualCommentingStore.removeReply(createReplyInput.commentId, temporaryReply.id);
    }
  };

  const readCommentsAndReplies = async (
    payload: MaybeRef<SetContextualCommentAndRepliesReadbyInput>,
    recipientId: MaybeRef<string>
  ) => {
    const { commentIds, replies } = unref(payload) as SetContextualCommentAndRepliesReadbyInput;
    const nonTemporaryCommentIds = commentIds.filter(
      (commentId: string) =>
        !CommentsHelper.isCommentTemporary(contextualCommentingStore.comments, commentId)
    );

    const readEvent = CommentsFactory.readEvent({
      participant: CommentsFactory.commentParticipant({
        recipientId: unref(recipientId) as string,
      }),
    });

    const staleComments = [...contextualCommentingStore.comments];

    nonTemporaryCommentIds.forEach(commentId => {
      contextualCommentingStore.addReadEvent(commentId, readEvent);
    });

    replies.forEach(({ commentId, replyIds }) => {
      replyIds.forEach(replyId => {
        contextualCommentingStore.addReplyReadEvent(commentId, replyId, readEvent);
      });
    });

    try {
      await CommentsService.setReadBy(unref({ commentIds: nonTemporaryCommentIds, replies }));
    } catch (e) {
      contextualCommentingStore.setComments([...staleComments]);
    }
  };

  const selectComment = (commentId: MaybeRef<string>, recipientId: MaybeRef<string>) => {
    contextualCommentingStore.selectComment(unref(commentId as string));
    if (!commentId) {
      return;
    }
    const _recipientId = unref(recipientId);
    const isCommentTemporary = CommentsHelper.isCommentTemporary(
      contextualCommentingStore.comments,
      contextualCommentingStore.selectedCommentId
    );

    if (!_recipientId || isCommentTemporary) {
      return;
    }

    const isCommentUnread = !CommentsHelper.isReadByRecipient(
      contextualCommentingStore.selectedComment.reads,
      _recipientId as string
    );
    const unreadReplies = CommentsHelper.getUnreadRepliesByRecipient(
      contextualCommentingStore.selectedComment.replies,
      _recipientId as string
    );

    if (!isCommentUnread && !unreadReplies.length) {
      return;
    }

    const commentAndRepliesReadbyInput: SetContextualCommentAndRepliesReadbyInput = {
      commentIds: isCommentUnread ? [contextualCommentingStore.selectedCommentId] : [],
      replies: unreadReplies.length
        ? [
            {
              commentId: contextualCommentingStore.selectedCommentId,
              replyIds: unreadReplies.map(({ id }) => id),
            },
          ]
        : [],
    };
    readCommentsAndReplies(commentAndRepliesReadbyInput, _recipientId);
  };

  const deleteComment = async (commentId: string) => {
    if (contextualCommentingStore.selectedCommentId === commentId) {
      contextualCommentingStore.selectComment('');
    }
    if (CommentsHelper.isCommentTemporary(contextualCommentingStore.comments, commentId)) {
      return;
    }
    const staleComments = [...contextualCommentingStore.viewableComments];
    contextualCommentingStore.removeComment(commentId);
    try {
      await CommentsService.delete(commentId);
    } catch (e) {
      contextualCommentingStore.setComments([...staleComments]);
      console.error('Could not delete comment');
    }
  };
  return {
    loadComments,
    createComment,
    updateComment,
    readCommentsAndReplies,
    createReply,
    selectComment,
    deleteComment,
  };
};
